/* eslint-disable no-unused-vars */
import React, {
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { events, invoke, showFlag, view } from "@forge/bridge";
import { Text } from "@atlaskit/primitives";
import ReactDOM from "react-dom";
import { v4 as uuid } from "uuid";
import { useTranslation } from "react-i18next";

import debounce from "lodash.debounce";
import sortBy from "lodash/sortBy";
import { Entry } from "../ForgeContextWrapper";
import { documentReady } from "../Shared/functions";
import "./css.css";
import SuspenseFallback from "../Shared/Components/SuspenseFallback";
import { openCatalogBrowser } from "../Shared/openers";
import { AppContext } from "../Context";
import { LOCAL_STORAGE_CARTS_KEY } from "../AdminPage/CatalogCustomiser/consts";
import { CART_CUTOFF_TIME_IN_MILLIS } from "../Shared/shopHandlers";

const storeCartLocally = (cart) => {
  if (!cart) return;
  const cutoffTimestamp = new Date().getTime();
  const locallySavedCarts = JSON.parse(
    localStorage.getItem(LOCAL_STORAGE_CARTS_KEY) || "[]"
  );
  const sanitizedCarts = (locallySavedCarts || [])
    .filter(
      ({ id, catalogId, timestamp }) =>
        id !== cart.id &&
        catalogId !== cart.catalogId &&
        cutoffTimestamp - timestamp < CART_CUTOFF_TIME_IN_MILLIS
    )
    .map(
      ({ id, timestamp, catalogId }) => ({
        id,
        timestamp,
        catalogId,
      }),
      (item) => item.timestamp
    );
  localStorage.setItem(
    LOCAL_STORAGE_CARTS_KEY,
    JSON.stringify(
      sortBy(
        [
          ...sanitizedCarts,
          {
            catalogId: cart.catalogId,
            id: cart.id,
            timestamp: cutoffTimestamp,
          },
        ],
        (item) => item.timestamp
      )
    )
  );
};

function RequestCreate() {
  const { catalog, generalStyling, savedCarts = [] } = useContext(AppContext);

  const [cart, setCart] = useState();

  const { items } = cart || {};

  const { t } = useTranslation();

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      if (!items?.length) {
        return;
      }
      e.returnValue = true;
    };

    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [items]);

  const [totalPrice, pricingErrors] = useMemo(
    () =>
      (items || []).reduce(
        ([total, errors = []], { count, layout: { price } = {} }) => {
          try {
            return [total + Number(price || 0) * count, errors];
          } catch (e) {
            return [total, [...errors, e.message]];
          }
        },
        [0, []]
      ),
    [items]
  );

  useEffect(() => {
    if (Number.isNaN(totalPrice) || !!pricingErrors.length)
      showFlag({
        id: `pricing_error-${uuid()}`,
        type: "warning",
        title: t("cant-price-all-items"),
        description:
          t("some-or-all-prices-couldnt-be-summed") + pricingErrors.join("; "),
        isAutoDismiss: true,
      });
  }, [totalPrice, pricingErrors]);

  const shouldAllowSubmit = catalog?.productsInCartRequired
    ? !!items?.length
    : true;

  const simplifiedCartItems = useMemo(
    () =>
      items?.map(
        ({
          assetId,
          id,
          label,
          status,
          count,
          name: defaultName,
          categoryId,
          layout: { price, name: mappedName },
        }) => ({
          assetId,
          id,
          status,
          count,
          categoryId,
          name: mappedName || defaultName || label,
          price: price || 0,
        })
      ) || [],
    [items]
  );

  useEffect(() => {
    if (!cart?.id) return;
    invoke("saveCart", cart);
  }, [simplifiedCartItems]);

  const debounceOnClose = useCallback(
    debounce(() => {
      showFlag({
        id: "cart-saved-flag",
        title: t("cart-is-saved"),
        type: "success",
        description: t(
          "your-cart-is-saved-and-will-be-submitted-with-the-request-form"
        ),
        isAutoDismiss: true,
      });
    }, 200),
    []
  );

  const openCatalog = useCallback(
    (options = {}) =>
      openCatalogBrowser({
        catalog,
        cart,
        generalStyling,
        onClose: debounceOnClose,
        ...options,
      }),
    [cart, catalog, generalStyling]
  );

  useEffect(() => {
    const openDialogSub = events.on("OPEN_CATALOG_BROWSER", openCatalog);
    return () => {
      openDialogSub.then((sub) => sub.unsubscribe());
    };
  }, [openCatalog]);

  useEffect(() => {
    const _sub = events.on("REQUEST_CATALOG", () => {
      if (!catalog) return;
      events.emit("SET_CATALOG", { catalog });
    });
    return () => {
      _sub.then((sub) => sub.unsubscribe());
    };
  }, [catalog]);

  useEffect(() => {
    const _sub = events.on("REQUEST_CART", () => {
      if (!cart) return;
      events.emit("SET_CART", {
        cart,
        source: "portal-header",
      });
    });
    return () => {
      _sub.then((sub) => sub.unsubscribe());
    };
  }, [cart]);

  useEffect(() => {
    events.on("SET_CART", (payload) => {
      if (payload.source === "portal-header") return;
      setCart(payload?.cart);
      storeCartLocally(payload?.cart);
    });
    events.emit("SET_CATALOG", { catalog });
  }, []);

  useEffect(() => {
    if (!catalog) return;
    const validCart = sortBy(savedCarts, (item) => item.timestamp).find(
      ({ catalogId }) => catalog?.id === catalogId
    );
    const cartToUse = validCart || {
      id: uuid(),
      catalogId: catalog.id,
      timestamp: new Date().getTime(),
      items: [],
    };
    // setCart(cartToUse);
    events.emit("SET_CART", {
      cart: cartToUse,
      source: "portal-header-saved-carts",
    });
  }, []);

  useEffect(() => {
    if (!catalog || !cart?.id) return;
    const submission = {
      cart: {
        ...cart,
        items: simplifiedCartItems,
        currency: catalog?.currencySymbol || catalog?.currency || "",
        totalPrice,
        catalogId: catalog.id,
        version: 2,
      },
      unlicensedAccess: ["unlicensed", "customer", "anonymous"],
      isValid: shouldAllowSubmit,
    };

    view.submit(submission).catch(() => {});
  }, [simplifiedCartItems, shouldAllowSubmit]);

  if (shouldAllowSubmit) return <></>;

  return (
    <Text color="color.text.accent.red">{t("products-in-cart-error")}</Text>
  );
}

documentReady(() => {
  const validCartsIds = JSON.parse(
    localStorage.getItem(LOCAL_STORAGE_CARTS_KEY) || "[]"
  )
    .map(({ id }) => id)
    .filter(Boolean);
  ReactDOM.render(
    <Entry
      showSpinnerWhileLoading={false}
      requiresLicense
      initFunctions={[
        { name: "catalog", fKey: "getCatalogForPortal" },
        { name: "generalStyling", fKey: "getGeneralStyling" },
        {
          name: "savedCarts",
          fKey: "getCartsByIds",
          data: {
            ids: validCartsIds,
          },
        },
      ]}
    >
      <RequestCreate />
    </Entry>,
    document.getElementById("root")
  );
});
